import React, { useEffect, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import gql from "graphql-tag";
import PlayerPosition from "./PlayerPosition";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRotate,
  faCalendarDays,
  faPeopleGroup,
} from "@fortawesome/free-solid-svg-icons";

function WaiversRecord(props) {
  const { seasonId, gameweek } = props;

  const [processedWaiversList, setProcessedWaiversList] = useState([]);
  const [rejectedWaiversList, setRejectedWaiversList] = useState([]);
  const [expandedWaivers, setExpandedWaivers] = useState(new Set());

  const FETCH_WAIVERS_RECORD = gql`
    query ($seasonId: String!, $gameweek: Int!) {
      getWaiversRecord(seasonId: $seasonId, gameweek: $gameweek) {
        _id
        SeasonId
        Gameweek
        TeamWaivers {
          TeamId
          TeamName
          PositionInTeamsWaiver
          PlayerToDrop {
            Position
            WebName
          }
          PlayerToPickUp {
            Position
            WebName
          }
          Status
          RejectionReason
          OrderProcessed
          AlreadyTakenByInfo {
            AlreadyTakenByWaiverOrder
            AlreadyTakenByTeamName
            AlreadyTakenByTeamId
          }
        }
      }
    }
  `;

  const [
    loadWaiversRecord,
    { loading, data: { getWaiversRecord: waiversRecord } = {}, error },
  ] = useLazyQuery(FETCH_WAIVERS_RECORD, {
    variables: { seasonId, gameweek },
    errorPolicy: 'all',
    onError: (error) => {
      console.log('Waivers record error:', error);
    },
  });

  useEffect(() => {
    if (seasonId && (gameweek || gameweek === 0)) {
      // Clear previous data when starting new query
      setProcessedWaiversList([]);
      setRejectedWaiversList([]);
      loadWaiversRecord();
    }
  }, [seasonId, gameweek]);

  useEffect(() => {
    if (waiversRecord) {
      setProcessedWaiversList([
        ...waiversRecord.TeamWaivers.filter((tw) => tw.OrderProcessed > 0),
      ]);
      setRejectedWaiversList([
        ...waiversRecord.TeamWaivers.filter((tw) => tw.OrderProcessed === 0),
      ]);
    }
  }, [waiversRecord]);

  useEffect(() => {
    if (error) {
      // Clear data when there's an error
      setProcessedWaiversList([]);
      setRejectedWaiversList([]);
    }
  }, [error]);

  const waiverClass = (status) => {
    if (status === "Processed") {
      return "waiver-processed";
    }

    return "waiver-rejected";
  };

  const waiverRejectionReason = (status, reason) => {
    if (status === "Processed") {
      return;
    }

    if (reason === "InvalidatesLineups") {
      return faCalendarDays;
    }

    if (reason === "TooManyFromTeam") {
      return faPeopleGroup;
    }

    return faRotate;
  };

  const toggleRejectedWaivers = (orderProcessed) => {
    const newExpanded = new Set(expandedWaivers);
    if (newExpanded.has(orderProcessed)) {
      newExpanded.delete(orderProcessed);
    } else {
      newExpanded.add(orderProcessed);
    }
    setExpandedWaivers(newExpanded);
  };

  return (
    <table className="waiver-record">
      <thead>
        <tr>
          <th></th>
          <th>Team</th>
          <th>Out</th>
          <th></th>
          <th>In</th>
          <th></th>
          <th>?</th>
          <th>
            <FontAwesomeIcon icon={faRotate} />
          </th>
        </tr>
      </thead>

      <tbody>
        {loading ? (
          <tr>
            <td colSpan="8">Loading table...</td>
          </tr>
        ) : error ? (
          <tr>
            <td colSpan="8" className="table-message">
              No waiver records found for gameweek {gameweek}
            </td>
          </tr>
        ) : (
          processedWaiversList &&
          processedWaiversList.length > 0 ? (
            processedWaiversList
            .sort((a, b) => a.OrderProcessed - b.OrderProcessed)
            .map((pw, index) => {
              const rejectedForThisWaiver = rejectedWaiversList.filter(
                (rw) =>
                  rw.AlreadyTakenByInfo.AlreadyTakenByWaiverOrder ===
                  pw.OrderProcessed
              );
              const rejectedCount = rejectedForThisWaiver.length;
              const isExpanded = expandedWaivers.has(pw.OrderProcessed);

              return (
                <React.Fragment key={index}>
                  <tr 
                    className={`${waiverClass(pw.Status)} ${rejectedCount > 0 ? 'clickable-row' : ''}`}
                    onClick={rejectedCount > 0 ? () => toggleRejectedWaivers(pw.OrderProcessed) : undefined}
                    style={rejectedCount > 0 ? { cursor: 'pointer' } : {}}
                  >
                    <td>{pw.OrderProcessed}</td>
                    <td>{pw.TeamName}</td>
                    <td>
                      <PlayerPosition positionNumber={pw.PlayerToDrop.Position} />
                    </td>
                    <td>{pw.PlayerToDrop.WebName}</td>
                    <td>
                      <PlayerPosition
                        positionNumber={pw.PlayerToPickUp.Position}
                      />
                    </td>
                    <td>{pw.PlayerToPickUp.WebName}</td>
                    <td>{pw.PositionInTeamsWaiver}</td>
                    <td>
                      {pw.Status === "Rejected" ? (
                        <FontAwesomeIcon
                          icon={waiverRejectionReason(
                            pw.Status,
                            pw.RejectionReason
                          )}
                          size="lg"
                        />
                      ) : rejectedCount > 0 ? (
                        rejectedCount
                      ) : (
                        <span></span>
                      )}
                    </td>
                  </tr>
                  {isExpanded && rejectedForThisWaiver
                    .sort(
                      (a, b) => {
                        // First sort by whether they're from the same team (different teams first)
                        const aIsSameTeam = a.TeamName === pw.TeamName;
                        const bIsSameTeam = b.TeamName === pw.TeamName;
                        if (aIsSameTeam !== bIsSameTeam) {
                          return aIsSameTeam ? 1 : -1; // Same team goes to bottom
                        }
                        // Then by position in team's waiver list
                        if (a.PositionInTeamsWaiver !== b.PositionInTeamsWaiver) {
                          return a.PositionInTeamsWaiver - b.PositionInTeamsWaiver;
                        }
                        // Finally by team name
                        return a.TeamName.localeCompare(b.TeamName);
                      }
                    )
                    .map((rw, rwIndex) => (
                      <tr 
                        key={`${pw.OrderProcessed}_${rwIndex}`}
                        style={rw.TeamName === pw.TeamName ? { opacity: 0.5 } : {}}
                      >
                        <td></td>
                        <td>{rw.TeamName}</td>
                        <td>
                          <PlayerPosition
                            positionNumber={rw.PlayerToDrop.Position}
                          />
                        </td>
                        <td>{rw.PlayerToDrop.WebName}</td>
                        <td>
                          <PlayerPosition
                            positionNumber={rw.PlayerToPickUp.Position}
                          />
                        </td>
                        <td>{rw.PlayerToPickUp.WebName}</td>
                        <td>{rw.PositionInTeamsWaiver}</td>
                        <td>
                          <FontAwesomeIcon
                            icon={waiverRejectionReason(
                              rw.Status,
                              rw.RejectionReason
                            )}
                            size="lg"
                          />
                        </td>
                      </tr>
                    ))}
                </React.Fragment>
              );
            })
          ) : (
            <tr>
              <td colSpan="8" className="table-message">
                No waiver records found for gameweek {gameweek}
              </td>
            </tr>
          )
        )}
      </tbody>
    </table>
  );
}

export default WaiversRecord;
